// client/src/components/MaterialUploadModal.js — upload a partner material
// into a mandate's slot (POST /api/mandates/:id/materials/:type/upload).
// The CRM is where materials are loaded and holds only the current version
// of each; Google Drive keeps the full history (Travis, 10/23).
//
// Every slot the mandate's work type has is listed as Filled (with the
// current file) or Empty, so it is clear whether an upload is new or
// replaces a file. Replacing asks first; the replaced file is removed from
// the CRM. Opened from a slot card (mandate and slot preset) or from the
// Mandate Pipeline page (both chosen here).
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { Modal } from './people/ui'

const muted = { color: 'var(--text-muted)' }
const label = { fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', ...muted, marginBottom: 6 }

export default function MaterialUploadModal({ mandateId: presetMandate, materialType: presetType, onClose }) {
  const qc = useQueryClient()
  const [mandateId, setMandateId] = useState(presetMandate ?? '')
  const [materialType, setMaterialType] = useState(presetType ?? '')
  const [file, setFile] = useState(null)
  const [confirming, setConfirming] = useState(false)
  const [done, setDone] = useState(null)

  const { data: mandates = [] } = useQuery({
    queryKey: ['mandates'],
    queryFn: () => api.get('/api/mandates').then(r => r.data.data ?? []),
    enabled: !presetMandate,
  })
  const { data: materials, isLoading } = useQuery({
    queryKey: ['materials', mandateId],
    queryFn: () => api.get(`/api/mandates/${mandateId}/materials`).then(r => r.data.data),
    enabled: !!mandateId,
  })
  const slots = (materials?.sets ?? []).flatMap(s => s.slots)
  const slot = slots.find(s => s.material_type === materialType)
  const existing = slot?.document ?? null

  const upload = useMutation({
    mutationFn: () => {
      const form = new FormData()
      form.append('file', file)
      if (existing) form.append('replace', 'true')
      return api.post(`/api/mandates/${mandateId}/materials/${encodeURIComponent(materialType)}/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then(r => r.data.data)
    },
    onSuccess: data => {
      setConfirming(false)
      setDone(data)
      qc.invalidateQueries({ queryKey: ['materials', mandateId] })
      qc.invalidateQueries({ queryKey: ['documents', mandateId] })
    },
    onError: () => setConfirming(false),
  })

  function submit(e) {
    e.preventDefault()
    if (!file || !slot) return
    if (existing && !confirming) { setConfirming(true); return }
    upload.mutate()
  }

  const activeMandates = mandates.filter(m => m.status === 'Active')
    .sort((a, b) => a.mandate_name.localeCompare(b.mandate_name))

  if (done) {
    return (
      <Modal title="Upload Materials" onClose={onClose} width={520}>
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13 }}>
          <div>
            {done.file_name} is now the {done.material_type}.
            {done.replaced && <> It replaced {done.replaced}, which was removed from the CRM.</>}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => { setDone(null); setFile(null); setMaterialType('') }}>Upload another</button>
            <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal title="Upload Materials" onClose={onClose} width={560}>
      <form onSubmit={submit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
        {!presetMandate && (
          <div>
            <div style={label}>Mandate</div>
            <select className="select" style={{ width: '100%' }} value={mandateId}
              onChange={e => { setMandateId(e.target.value); setMaterialType(''); setConfirming(false) }}>
              <option value="">Choose a mandate…</option>
              {activeMandates.map(m => <option key={m.mandate_id} value={m.mandate_id}>{m.mandate_name}</option>)}
            </select>
          </div>
        )}

        {mandateId && (
          <div>
            <div style={label}>Material</div>
            {isLoading ? <div style={{ fontSize: 13, ...muted }}>Loading…</div>
              : !slots.length ? <div style={{ fontSize: 13, ...muted }}>No materials are set up for this mandate's work type.</div>
              : (
                <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  {slots.map((s, i) => {
                    const on = s.material_type === materialType
                    return (
                      <label key={s.material_type} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer',
                        borderTop: i ? '1px solid var(--border)' : 'none',
                        background: on ? 'var(--hover-tint, rgba(255,255,255,0.04))' : 'transparent',
                      }}>
                        <input type="radio" name="material" checked={on}
                          onChange={() => { setMaterialType(s.material_type); setConfirming(false) }} />
                        <span style={{ fontSize: 13, fontWeight: 500, minWidth: 150 }}>{s.material_type}</span>
                        {s.document
                          ? <span style={{ fontSize: 12, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.document.file_name}>
                              <span className="badge" style={{ marginRight: 8 }}>Filled</span>{s.document.file_name}
                            </span>
                          : <span style={{ fontSize: 12, ...muted }}>Empty</span>}
                      </label>
                    )
                  })}
                </div>
              )}
          </div>
        )}

        {slot && (
          <div>
            <div style={label}>File</div>
            <input type="file" accept=".pdf,application/pdf,.pptx,.docx"
              onChange={e => { setFile(e.target.files?.[0] ?? null); setConfirming(false) }} />
            <div style={{ fontSize: 12, ...muted, marginTop: 6 }}>PDF shows a first-page preview; other files upload without one.</div>
          </div>
        )}

        {confirming && existing && (
          <div style={{ background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8,
            padding: '10px 14px', fontSize: 13, color: 'var(--danger-text)' }}>
            {materialType} already exists ({existing.file_name}). Overwrite? The current file is removed from the CRM; Drive keeps the history.
          </div>
        )}
        {upload.isError && (
          <div style={{ fontSize: 12, color: 'var(--danger-text)' }}>{upload.error.response?.data?.error || upload.error.message}</div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={confirming ? () => setConfirming(false) : onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={!file || !slot || upload.isPending}>
            {upload.isPending ? 'Uploading…' : confirming ? 'Overwrite' : existing ? 'Replace' : 'Upload'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
